/* eslint-disable max-lines */
import { getOrderShipmentsApi, getShipmentCountsBatchApi } from 'api/order';
import { shipmentModuleActions } from 'store/slices/shipmentModuleSlice';
import { selectEffectiveShipmentFilter } from 'store/selectors/shipmentModuleSelectors';
import {
  getCreatedAfterForCurrentWindow,
  getStartOfToday,
  getStartOfYesterday,
} from 'store/utils/shipmentUtils';
import { ShipmentStatus } from 'enums/common.enum';
import { handleError } from './errorHandlerThunks';

const PAGE_SIZE = 25;

// Tracks the most recent list fetch so stale responses don't overwrite newer tab/facility data.
let activeListRequestId = 0;
let activeCountsRequestId = 0;
let activePresetRequestId = 0;

/**
 * Builds the base filter for preset count queries from current category/subcategory state.
 */
const buildPresetBaseFilter = (state) => {
  const { activeCategory, activeSubcategory, activeFacility } = state.shipmentModule;
  const baseFilter = {};

  if (activeCategory === 'awaitingShipment') {
    baseFilter.shipmentStatus = [ShipmentStatus.REQUEST_CREATED];
    if (activeSubcategory !== 'all') {
      baseFilter.fulfillingPharmacyIds = [activeSubcategory];
    }
  } else if (activeCategory === 'shipped') {
    // For "voided" view we intentionally rely ONLY on isVoided flag (no shipmentStatus filter)
    if (activeSubcategory !== 'voided') {
      baseFilter.shipmentStatus = [ShipmentStatus.REQUEST_SHIPPED];
    }
    if (activeSubcategory === 'voided') {
      baseFilter.isVoided = true;
    } else {
      baseFilter.isVoided = false;
      if (activeSubcategory === 'today') {
        baseFilter.shippedDateAfter = getStartOfToday();
      } else if (activeSubcategory === 'yesterday') {
        baseFilter.shippedDateAfter = getStartOfYesterday();
        baseFilter.shippedDateBefore = getStartOfToday();
      } else if (activeSubcategory === 'recent') {
        baseFilter.shippedDateAfter = getCreatedAfterForCurrentWindow();
      }
    }
    if (activeFacility !== 'all') {
      baseFilter.fulfillingPharmacyIds = [activeFacility];
    }
  }

  return baseFilter;
};

/**
 * Fetches all counts (sidebar + presets) in a single batch API call.
 * Replaces the previous fetchShipmentCounts + fetchPresetCounts with 1 round-trip.
 */
export const fetchAllCounts = () => (dispatch, getState) => {
  // eslint-disable-next-line no-plusplus
  const countsReqId = ++activeCountsRequestId;
  // eslint-disable-next-line no-plusplus
  const presetReqId = ++activePresetRequestId;
  dispatch(shipmentModuleActions.setCountsLoading(true));
  dispatch(shipmentModuleActions.setPresetCountsLoading(true));

  const state = getState();
  const baseFilter = buildPresetBaseFilter(state);

  const queries = [
    // Sidebar category counts
    {
      key: 'awaiting',
      filter: { shipmentStatus: [ShipmentStatus.REQUEST_CREATED] },
      groupBy: ['fulfillingPharmacyId'],
    },
    {
      key: 'shipped',
      filter: { shipmentStatus: [ShipmentStatus.REQUEST_SHIPPED], isVoided: false },
      groupBy: ['fulfillingPharmacyId'],
    },
    {
      key: 'today',
      filter: {
        shipmentStatus: [ShipmentStatus.REQUEST_SHIPPED],
        isVoided: false,
        shippedDateAfter: getStartOfToday(),
      },
      groupBy: ['fulfillingPharmacyId'],
    },
    {
      key: 'yesterday',
      filter: {
        shipmentStatus: [ShipmentStatus.REQUEST_SHIPPED],
        isVoided: false,
        shippedDateAfter: getStartOfYesterday(),
        shippedDateBefore: getStartOfToday(),
      },
      groupBy: ['fulfillingPharmacyId'],
    },
    {
      key: 'recent',
      filter: {
        shipmentStatus: [ShipmentStatus.REQUEST_SHIPPED],
        isVoided: false,
        shippedDateAfter: getCreatedAfterForCurrentWindow(),
      },
      groupBy: ['fulfillingPharmacyId'],
    },
    {
      key: 'voided',
      filter: { isVoided: true },
      groupBy: ['fulfillingPharmacyId'],
    },
    // Preset tab counts
    { key: 'presetAll', filter: baseFilter },
    {
      key: 'presetCurrent',
      filter: { ...baseFilter, createdAfter: getCreatedAfterForCurrentWindow() },
    },
    {
      key: 'presetBadAddresses',
      filter: { ...baseFilter, addressValidity: ['WARN', 'INVALID', 'BLOCKED'] },
    },
    {
      key: 'presetManual',
      filter: { ...baseFilter, isManualShipment: true },
    },
  ];

  return getShipmentCountsBatchApi({ queries })
    .then((results) => {
      if (countsReqId !== activeCountsRequestId) return;

      const byKey = {};
      results.forEach((r) => {
        byKey[r.key] = r;
      });

      // Parse sidebar counts
      const awaitingByPharmacy = {};
      (byKey.awaiting?.buckets || []).forEach((b) => {
        if (b.fulfillingPharmacyId) {
          awaitingByPharmacy[b.fulfillingPharmacyId] = b.count;
        }
      });

      const shippedByPharmacy = {};
      (byKey.shipped?.buckets || []).forEach((b) => {
        if (b.fulfillingPharmacyId) {
          shippedByPharmacy[b.fulfillingPharmacyId] = b.count;
        }
      });

      const voidedByPharmacy = {};
      (byKey.voided?.buckets || []).forEach((b) => {
        if (b.fulfillingPharmacyId) {
          voidedByPharmacy[b.fulfillingPharmacyId] = b.count;
        }
      });

      const todayByPharmacy = {};
      (byKey.today?.buckets || []).forEach((b) => {
        if (b.fulfillingPharmacyId) {
          todayByPharmacy[b.fulfillingPharmacyId] = b.count;
        }
      });

      const yesterdayByPharmacy = {};
      (byKey.yesterday?.buckets || []).forEach((b) => {
        if (b.fulfillingPharmacyId) {
          yesterdayByPharmacy[b.fulfillingPharmacyId] = b.count;
        }
      });

      const recentByPharmacy = {};
      (byKey.recent?.buckets || []).forEach((b) => {
        if (b.fulfillingPharmacyId) {
          recentByPharmacy[b.fulfillingPharmacyId] = b.count;
        }
      });

      dispatch(
        shipmentModuleActions.setCounts({
          awaiting: {
            total: byKey.awaiting?.totalCount || 0,
            byPharmacy: awaitingByPharmacy,
          },
          shipped: {
            total: byKey.shipped?.totalCount || 0,
            all: byKey.shipped?.totalCount || 0,
            today: byKey.today?.totalCount || 0,
            yesterday: byKey.yesterday?.totalCount || 0,
            recent: byKey.recent?.totalCount || 0,
            voided: byKey.voided?.totalCount || 0,
            byPharmacy: shippedByPharmacy,
            todayByPharmacy,
            yesterdayByPharmacy,
            recentByPharmacy,
            voidedByPharmacy,
          },
        })
      );

      if (presetReqId !== activePresetRequestId) return;

      // Parse preset counts
      dispatch(
        shipmentModuleActions.setPresetCounts({
          all: byKey.presetAll?.totalCount || 0,
          current: byKey.presetCurrent?.totalCount || 0,
          badAddresses: byKey.presetBadAddresses?.totalCount || 0,
          manual: byKey.presetManual?.totalCount || 0,
        })
      );
    })
    .catch((error) => {
      if (countsReqId !== activeCountsRequestId) return;
      dispatch(shipmentModuleActions.setCountsLoading(false));
      dispatch(shipmentModuleActions.setPresetCountsLoading(false));
      dispatch(handleError({ error }));
    });
};

/**
 * Fetches preset tab counts (All, Current, Bad Addresses) scoped to current category/subcategory.
 * Used by navigation thunks — sends 3 queries in a single batch call.
 */
export const fetchPresetCounts = () => (dispatch, getState) => {
  // eslint-disable-next-line no-plusplus
  const requestId = ++activePresetRequestId;
  dispatch(shipmentModuleActions.setPresetCountsLoading(true));

  const state = getState();
  const baseFilter = buildPresetBaseFilter(state);

  const queries = [
    { key: 'presetAll', filter: baseFilter },
    {
      key: 'presetCurrent',
      filter: { ...baseFilter, createdAfter: getCreatedAfterForCurrentWindow() },
    },
    {
      key: 'presetBadAddresses',
      filter: { ...baseFilter, addressValidity: ['WARN', 'INVALID', 'BLOCKED'] },
    },
    {
      key: 'presetManual',
      filter: { ...baseFilter, isManualShipment: true },
    },
  ];

  return getShipmentCountsBatchApi({ queries })
    .then((results) => {
      if (requestId !== activePresetRequestId) return;

      const byKey = {};
      results.forEach((r) => {
        byKey[r.key] = r;
      });

      dispatch(
        shipmentModuleActions.setPresetCounts({
          all: byKey.presetAll?.totalCount || 0,
          current: byKey.presetCurrent?.totalCount || 0,
          badAddresses: byKey.presetBadAddresses?.totalCount || 0,
          manual: byKey.presetManual?.totalCount || 0,
        })
      );
    })
    .catch((error) => {
      if (requestId !== activePresetRequestId) return;
      dispatch(shipmentModuleActions.setPresetCountsLoading(false));
      dispatch(handleError({ error }));
    });
};

/**
 * Fetches shipments for a specific page using the computed effective filter.
 */
export const fetchShipmentsByPage =
  ({ page }) =>
  (dispatch, getState) => {
    // eslint-disable-next-line no-plusplus
    const requestId = ++activeListRequestId;
    const state = getState();
    const effectiveFilter = selectEffectiveShipmentFilter(state);
    const { sortBy } = state.shipmentModule.list;
    const limit = PAGE_SIZE;
    const offset = (page - 1) * limit;

    dispatch(shipmentModuleActions.setCurrentPage(page));
    dispatch(shipmentModuleActions.setListLoading(true));

    return getOrderShipmentsApi({ limit, offset, sortBy, filter: effectiveFilter })
      .then(({ count, result }) => {
        // Ignore stale responses from previous tab/facility requests.
        if (requestId !== activeListRequestId) {
          return;
        }
        dispatch(shipmentModuleActions.loadListPage({ page, items: result, count }));
      })
      .catch((error) => {
        if (requestId !== activeListRequestId) {
          return;
        }
        dispatch(shipmentModuleActions.setListLoading(false));
        dispatch(handleError({ error }));
      });
  };

/**
 * Navigates to a category and fetches data + preset counts.
 */
export const navigateToCategory =
  ({ category }) =>
  (dispatch) => {
    dispatch(shipmentModuleActions.setActiveCategory(category));
    dispatch(fetchPresetCounts());
    dispatch(fetchShipmentsByPage({ page: 1 }));
  };

/**
 * Navigates to a subcategory and fetches data + preset counts.
 */
export const navigateToSubcategory =
  ({ subcategory }) =>
  (dispatch) => {
    dispatch(shipmentModuleActions.setActiveSubcategory(subcategory));
    dispatch(fetchPresetCounts());
    dispatch(fetchShipmentsByPage({ page: 1 }));
  };

/**
 * Navigates directly to an "Awaiting Shipment" facility in the sidebar.
 * Ensures category and subcategory are updated before fetching, so the
 * effective filter always includes the correct fulfillingPharmacyId.
 */
export const navigateToAwaitingFacility =
  ({ pharmacyId }) =>
  (dispatch) => {
    dispatch(shipmentModuleActions.setAwaitingFacility(pharmacyId));
    dispatch(fetchPresetCounts());
    dispatch(fetchShipmentsByPage({ page: 1 }));
  };

/**
 * Navigates directly to a shipped subcategory (All, Recent, Voided) in the sidebar.
 * Ensures category and subcategory are updated in one step to avoid an extra fetch
 * with wrong filter (e.g. isVoided: false when navigating to voided).
 */
export const navigateToShippedSubcategory =
  ({ subcategory }) =>
  (dispatch) => {
    dispatch(shipmentModuleActions.setShippedSubcategory(subcategory));
    dispatch(fetchPresetCounts());
    dispatch(fetchShipmentsByPage({ page: 1 }));
  };

/**
 * Navigates to a facility tab in the content area.
 */
export const navigateToFacility =
  ({ pharmacyId }) =>
  (dispatch) => {
    dispatch(shipmentModuleActions.setActiveFacility(pharmacyId));
    dispatch(fetchPresetCounts());
    dispatch(fetchShipmentsByPage({ page: 1 }));
  };

/**
 * Sets active preset tab and fetches data.
 */
export const navigateToPreset =
  ({ preset }) =>
  (dispatch) => {
    dispatch(shipmentModuleActions.setActivePreset(preset));
    dispatch(fetchShipmentsByPage({ page: 1 }));
  };

/**
 * Refreshes after a mutation (create, cancel, void, delete, generate label).
 * Refetches current page + all counts in a single batch call.
 */
export const refreshAfterMutation = () => (dispatch, getState) => {
  const { currentPage } = getState().shipmentModule.list;
  dispatch(fetchAllCounts());
  dispatch(fetchShipmentsByPage({ page: currentPage }));
};

/**
 * Updates the list filter (e.g., flatSearch) and refetches.
 */
export const updateShipmentFilter =
  ({ filter }) =>
  (dispatch) => {
    dispatch(shipmentModuleActions.updateFilter(filter));
    dispatch(fetchShipmentsByPage({ page: 1 }));
  };

/**
 * Resets all list filters to initial state and refetches.
 */
export const resetShipmentFilter = () => (dispatch) => {
  dispatch(shipmentModuleActions.resetFilter());
  dispatch(fetchShipmentsByPage({ page: 1 }));
};

/**
 * Updates the list sort and refetches.
 */
export const updateShipmentSortBy =
  ({ sortBy }) =>
  (dispatch) => {
    dispatch(shipmentModuleActions.updateSortBy(sortBy));
    dispatch(fetchShipmentsByPage({ page: 1 }));
  };
